import { useMemo, useState } from 'react';
import { useApolloClient, useQuery } from '@apollo/client/react';
import { Link } from 'react-router';
import type { ColumnDef, SortingState, Updater } from '@tanstack/react-table';
import { graphql } from '@/gql';
import type {
  ExportLedgerTransactionsCsvQuery,
  LedgerTransactionFilter,
  LedgerTransactionSort,
  SortDirection,
} from '@/gql/graphql';
import type { TypedDocumentNode } from '@graphql-typed-document-node/core';
import { DataGrid } from '@/components/ui/DataGrid';
import { FilterBar } from '@/components/ui/FilterBar';
import { Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import { formatCurrency, formatDate, formatDateTime, formatShortId } from '@/lib/format';
import { downloadAuthenticatedCsv } from '@/lib/downloadCsv';
import { useListHref } from '@/hooks/useListLocation';
import {
  isDefault,
  sortingFromUrl,
  sortingToUrl,
  useDebouncedUrlField,
  useUrlState,
} from '@/hooks/useUrlState';
import {
  LEDGER_ADVANCED_KEYS,
  LEDGER_COLUMN_SORT,
  LEDGER_DEFAULT_SORT,
  LEDGER_METHOD_OPTIONS,
  LEDGER_PSP_OPTIONS,
  ledgerUrlSchema,
  type LedgerMethodValue,
  type LedgerPspValue,
} from './ledgerUrlState';

const LedgerTransactionsDoc = graphql(/* GraphQL */ `
  query LedgerTransactions(
    $filter: LedgerTransactionFilter
    $page: Int
    $pageSize: Int
    $sort: LedgerTransactionSort
  ) {
    ledgerTransactions(filter: $filter, page: $page, pageSize: $pageSize, sort: $sort) {
      nodes {
        id
        transactionId
        amountReceived
        amountTip
        amountCashTrade
        amountRecognizedTotal
        psp
        paymentMethod
        recognitionDate
        createdAt
        categoryName
        deviceName
        categoryId
        deviceId
        order {
          id
        }
        tab {
          id
          name
        }
        payment {
          id
        }
        refund {
          id
        }
      }
      pageInfo {
        page
        pageSize
        totalRows
        totalPages
      }
    }
  }
`);

const ExportLedgerTransactionsCsvDoc = graphql(/* GraphQL */ `
  query ExportLedgerTransactionsCsv($filter: LedgerTransactionFilter, $sort: LedgerTransactionSort) {
    exportLedgerTransactionsCsv(filter: $filter, sort: $sort) {
      url
      expiresAt
    }
  }
`) as TypedDocumentNode<ExportLedgerTransactionsCsvQuery>;

const filterSectionTitle =
  'mb-3 text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400';
const filterLabel = 'mb-1 block text-sm font-medium text-gray-700 dark:text-gray-200';
const filterHint = 'mt-1.5 text-xs leading-snug text-gray-500 dark:text-gray-400';

interface LedgerRow {
  id: string;
  transactionId: string;
  amountReceived: number;
  amountTip: number;
  amountCashTrade: number;
  amountRecognizedTotal: number;
  psp?: string | null;
  paymentMethod: string;
  recognitionDate: string;
  createdAt: string;
  categoryName?: string | null;
  deviceName?: string | null;
  categoryId?: string | null;
  deviceId?: string | null;
  order?: { id: string } | null;
  tab?: { id: string; name: string } | null;
  payment?: { id: string } | null;
  refund?: { id: string } | null;
}

/**
 * Read-only ledger list: filters map to GraphQL `LedgerTransactionFilter`.
 */
export function LedgerListPage() {
  const apollo = useApolloClient();
  const { state: url, patch, setFilters, reset } = useUrlState(ledgerUrlSchema);
  const listHref = useListHref();
  const [search, setSearch] = useDebouncedUrlField(url.search, (next) => setFilters({ search: next }));
  const [amountMin, setAmountMin] = useDebouncedUrlField(url.amountMin, (next) => setFilters({ amountMin: next }));
  const [amountMax, setAmountMax] = useDebouncedUrlField(url.amountMax, (next) => setFilters({ amountMax: next }));
  // The Filters panel opens on mount when a filter inside it is set (a reload, Back, an inbound link).
  const [filtersOpen] = useState(() => LEDGER_ADVANCED_KEYS.some((key) => !isDefault(ledgerUrlSchema, url, key)));
  const sorting = useMemo(() => sortingFromUrl(url), [url]);
  const handleSortingChange = (updater: Updater<SortingState>) => {
    const next = typeof updater === 'function' ? updater(sorting) : updater;
    setFilters(sortingToUrl(next, LEDGER_DEFAULT_SORT));
  };

  const filter: LedgerTransactionFilter = useMemo(() => {
    const out: LedgerTransactionFilter = {};
    const trimmed = url.search.trim();
    if (trimmed) {
      out.search = trimmed;
    }
    if (url.method) {
      out.paymentMethod = [url.method];
    }
    if (url.psp) {
      out.psp = [url.psp];
    }
    const rf = url.from ? `${url.from}T00:00:00` : null;
    const rt = url.to ? `${url.to}T23:59:59` : null;
    if (rf || rt) {
      out.recognitionDate = { from: rf, to: rt };
    }
    if (url.amountMin !== '' || url.amountMax !== '') {
      out.amount = {
        min: url.amountMin === '' ? null : Number(url.amountMin),
        max: url.amountMax === '' ? null : Number(url.amountMax),
      };
    }
    return out;
  }, [url]);

  const gqlSort = useMemo(
    (): LedgerTransactionSort => ({
      field: LEDGER_COLUMN_SORT[url.sort],
      direction: (url.dir === 'asc' ? 'ASC' : 'DESC') as SortDirection,
    }),
    [url.sort, url.dir],
  );

  const { data, previousData, loading } = useQuery(LedgerTransactionsDoc, {
    variables: { filter, page: url.page, pageSize: url.size, sort: gqlSort },
  });
  const ledgerData = data ?? previousData;

  const rows: LedgerRow[] = useMemo(
    () => ledgerData?.ledgerTransactions.nodes ?? [],
    [ledgerData],
  );
  const pageInfo = ledgerData?.ledgerTransactions.pageInfo;

  const columns = useMemo<ColumnDef<LedgerRow>[]>(
    () => [
      {
        id: 'transactionId',
        accessorKey: 'transactionId',
        header: 'Transaction',
        cell: ({ row }) => (
          <Link
            to={`/ledger/${row.original.id}`}
            state={{ from: listHref }}
            className="font-medium text-brand hover:underline"
          >
            {row.original.transactionId}
          </Link>
        ),
      },
      {
        id: 'method',
        accessorKey: 'paymentMethod',
        header: 'Method',
        cell: ({ row }) => row.original.paymentMethod,
      },
      {
        id: 'psp',
        accessorFn: (row) => row.psp ?? '',
        header: 'PSP',
        enableSorting: false,
        cell: ({ row }) => row.original.psp ?? '—',
      },
      {
        id: 'amount',
        accessorKey: 'amountReceived',
        header: 'Amount received',
        cell: ({ row }) => formatCurrency(row.original.amountReceived),
      },
      {
        id: 'tip',
        accessorKey: 'amountTip',
        header: 'Tip',
        cell: ({ row }) =>
          row.original.amountTip !== 0 ? formatCurrency(row.original.amountTip) : '—',
      },
      {
        id: 'recognized',
        accessorKey: 'amountRecognizedTotal',
        header: 'Recognized',
        enableSorting: false,
        cell: ({ row }) => formatCurrency(row.original.amountRecognizedTotal),
      },
      {
        id: 'order',
        accessorFn: (row) => row.order?.id ?? '',
        header: 'Order',
        enableSorting: false,
        cell: ({ row }) =>
          row.original.order ? (
            <Link to={`/orders/${row.original.order.id}`} state={{ from: listHref }} className="text-brand hover:underline">
              #{formatShortId(row.original.order.id)}
            </Link>
          ) : (
            '—'
          ),
      },
      {
        id: 'tab',
        accessorFn: (row) => row.tab?.name ?? row.tab?.id ?? '',
        header: 'Tab',
        enableSorting: false,
        cell: ({ row }) =>
          row.original.tab ? (
            <Link to={`/tabs/${row.original.tab.id}`} state={{ from: listHref }} className="text-brand hover:underline">
              {row.original.tab.name || `#${formatShortId(row.original.tab.id)}`}
            </Link>
          ) : (
            '—'
          ),
      },
      {
        id: 'payment',
        accessorFn: (row) => row.payment?.id ?? '',
        header: 'Payment',
        enableSorting: false,
        cell: ({ row }) =>
          row.original.payment ? (
            <Link
              to={`/payments?search=${encodeURIComponent(row.original.payment.id)}`}
              className="text-brand hover:underline"
            >
              #{formatShortId(row.original.payment.id)}
            </Link>
          ) : (
            '—'
          ),
      },
      {
        id: 'refund',
        accessorFn: (row) => row.refund?.id ?? '',
        header: 'Refund',
        enableSorting: false,
        cell: ({ row }) =>
          row.original.refund ? (
            <Link
              to={`/refunds?search=${encodeURIComponent(row.original.refund.id)}`}
              className="text-brand hover:underline"
            >
              #{formatShortId(row.original.refund.id)}
            </Link>
          ) : (
            '—'
          ),
      },
      {
        id: 'category',
        accessorFn: (row) => row.categoryName ?? row.categoryId ?? '',
        header: 'Category',
        cell: ({ row }) =>
          row.original.categoryId ? (
            <Link
              to={`/categories/${row.original.categoryId}`}
              className="text-brand hover:underline"
            >
              {row.original.categoryName || `#${formatShortId(row.original.categoryId)}`}
            </Link>
          ) : (
            '—'
          ),
      },
      {
        id: 'device',
        accessorFn: (row) => row.deviceName ?? row.deviceId ?? '',
        header: 'Device',
        cell: ({ row }) =>
          row.original.deviceId ? (
            <Link
              to={`/devices/${row.original.deviceId}`}
              className="text-brand hover:underline"
            >
              {row.original.deviceName || `#${formatShortId(row.original.deviceId)}`}
            </Link>
          ) : (
            '—'
          ),
      },
      {
        id: 'recognitionDate',
        accessorFn: (row) => row.recognitionDate,
        header: 'Recognition',
        cell: ({ row }) => formatDate(row.original.recognitionDate),
      },
      {
        id: 'createdAt',
        accessorKey: 'createdAt',
        header: 'Created',
        cell: ({ row }) => formatDateTime(row.original.createdAt),
      },
    ],
    [listHref],
  );

  const clearAllFilters = () => reset();

  const handleExport = async () => {
    const { data: exportData } = await apollo.query({
      query: ExportLedgerTransactionsCsvDoc,
      variables: { filter, sort: gqlSort },
      fetchPolicy: 'network-only',
    });
    if (exportData?.exportLedgerTransactionsCsv?.url) {
      await downloadAuthenticatedCsv(exportData.exportLedgerTransactionsCsv.url);
    }
  };

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">Ledger</h1>
        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
          Reporting transactions with recognized amounts and VAT breakdowns.
        </p>
      </div>

      <FilterBar
        defaultOpen={filtersOpen}
        search={{
          id: 'admin.ledger.search',
          value: search,
          onChange: setSearch,
          placeholder: 'Search by transaction id, device, category…',
        }}
        primary={
          <button
            type="button"
            id="admin.ledger.reset"
            onClick={clearAllFilters}
            className="whitespace-nowrap rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm font-medium text-gray-700 shadow-xs transition-colors hover:bg-gray-50 dark:border-white/10 dark:bg-gray-900 dark:text-gray-200 dark:hover:bg-gray-800"
          >
            Reset all filters
          </button>
        }
        advanced={
          <div className="w-full min-w-0 space-y-10">
            <section className="min-w-0" aria-labelledby="ledger-filters-basics-heading">
              <h3 id="ledger-filters-basics-heading" className={filterSectionTitle}>
                Method, PSP and recognition date
              </h3>
              <div className="grid w-full grid-cols-1 gap-x-6 gap-y-4 lg:grid-cols-12">
                <label className="min-w-0 lg:col-span-2">
                  <span className={filterLabel}>Payment method</span>
                  <Select
                    className="w-full min-w-0"
                    id="admin.ledger.method"
                    value={url.method}
                    onChange={(e) => setFilters({ method: e.target.value as LedgerMethodValue | '' })}
                    options={LEDGER_METHOD_OPTIONS}
                  />
                </label>
                <label className="min-w-0 lg:col-span-2">
                  <span className={filterLabel}>PSP</span>
                  <Select
                    className="w-full min-w-0"
                    id="admin.ledger.psp"
                    value={url.psp}
                    onChange={(e) => setFilters({ psp: e.target.value as LedgerPspValue | '' })}
                    options={LEDGER_PSP_OPTIONS}
                  />
                </label>
                <label className="min-w-0 lg:col-span-7">
                  <span className={filterLabel}>Recognition date (from → to)</span>
                  <div className="flex flex-col gap-3 sm:flex-row sm:items-stretch">
                    <Input
                      type="date"
                      value={url.from}
                      onChange={(e) => setFilters({ from: e.target.value })}
                      className="min-h-[2.5rem] w-full min-w-0 flex-1"
                    />
                    <span
                      className="hidden shrink-0 self-center text-sm text-gray-400 sm:inline dark:text-gray-500"
                      aria-hidden
                    >
                      →
                    </span>
                    <Input
                      type="date"
                      value={url.to}
                      onChange={(e) => setFilters({ to: e.target.value })}
                      className="min-h-[2.5rem] w-full min-w-0 flex-1"
                    />
                  </div>
                  <span className={filterHint}>
                    Uses each row&apos;s recognition (business) date, not the created timestamp.
                  </span>
                </label>
              </div>
            </section>

            <section
              className="min-w-0 border-t border-gray-200/90 pt-8 dark:border-white/10"
              aria-labelledby="ledger-filters-amounts-heading"
            >
              <h3 id="ledger-filters-amounts-heading" className={filterSectionTitle}>
                Amount received
              </h3>
              <div className="grid w-full grid-cols-2 gap-x-6 gap-y-4 md:grid-cols-4">
                <label className="min-w-0">
                  <span className={filterLabel}>Min</span>
                  <Input
                    type="number"
                    inputMode="decimal"
                    step="any"
                    value={amountMin}
                    onChange={(e) => setAmountMin(e.target.value)}
                    placeholder="Any"
                    className="w-full min-w-0"
                  />
                </label>
                <label className="min-w-0">
                  <span className={filterLabel}>Max</span>
                  <Input
                    type="number"
                    inputMode="decimal"
                    step="any"
                    value={amountMax}
                    onChange={(e) => setAmountMax(e.target.value)}
                    placeholder="Any"
                    className="w-full min-w-0"
                  />
                </label>
              </div>
            </section>
          </div>
        }
      />

      <DataGrid<LedgerRow>
        data={rows}
        columns={columns}
        loading={loading && !ledgerData}
        rowId={(row) => row.id}
        onExport={handleExport}
        sorting={{ state: sorting, onChange: handleSortingChange }}
        pagination={{
          page: pageInfo?.page ?? url.page,
          pageSize: pageInfo?.pageSize ?? url.size,
          totalRows: pageInfo?.totalRows ?? 0,
          totalPages: pageInfo?.totalPages ?? 1,
          onPageChange: (next) => patch({ page: next }),
          onPageSizeChange: (next) => setFilters({ size: next }),
        }}
      />
    </div>
  );
}
